const ATTRIBUTION_STORAGE_KEY = 'metaAttribution';
const COOKIE_FBP = '_fbp';
const COOKIE_FBC = '_fbc';

const safeWindow = typeof window !== 'undefined' ? window : null;

const getCookie = (name) => {
  if (!safeWindow?.document?.cookie) return '';
  const cookies = safeWindow.document.cookie.split(';').map((part) => part.trim());
  const match = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

const readJsonStorage = (key) => {
  if (!safeWindow?.localStorage) return {};
  try {
    const raw = safeWindow.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (_err) {
    return {};
  }
};

const writeJsonStorage = (key, value) => {
  if (!safeWindow?.localStorage) return;
  try {
    safeWindow.localStorage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    // Ignore storage errors.
  }
};

const readStoredUser = () => {
  const stored = readJsonStorage('user');
  return stored && typeof stored === 'object' ? stored : {};
};

const pruneObject = (value) => {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry === null || entry === undefined) return false;
      if (typeof entry === 'string') return entry.trim() !== '';
      if (Array.isArray(entry)) return entry.length > 0;
      return true;
    }),
  );
};

export const getMetaAttribution = () => {
  const stored = readJsonStorage(ATTRIBUTION_STORAGE_KEY);
  return pruneObject({
    ...stored,
    fbp: getCookie(COOKIE_FBP) || stored.fbp,
    fbc: getCookie(COOKIE_FBC) || stored.fbc,
  });
};

export const captureAttributionFromLocation = (search = safeWindow?.location?.search || '') => {
  if (!safeWindow) return {};
  const params = new URLSearchParams(search);
  const current = getMetaAttribution();
  const next = { ...current };

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value) next[key] = value;
  });

  const fbclid = params.get('fbclid');
  if (fbclid) {
    next.fbclid = fbclid;
    next.fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  if (Object.keys(next).length > 0) {
    writeJsonStorage(ATTRIBUTION_STORAGE_KEY, next);
  }

  return next;
};

export const generateMetaEventId = (prefix = 'meta') => (
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

const getEventSourceUrl = () => safeWindow?.location?.href || '';

const buildUserData = (overrides = {}) => {
  const storedUser = readStoredUser();
  const attribution = getMetaAttribution();
  return pruneObject({
    email: overrides.email || storedUser.email,
    mobile: overrides.mobile || storedUser.mobile,
    external_id: overrides.external_id || storedUser.id,
    client_user_agent: safeWindow?.navigator?.userAgent,
    fbp: overrides.fbp || attribution.fbp,
    fbc: overrides.fbc || attribution.fbc,
  });
};

const buildCustomData = (customData = {}) => pruneObject({
  ...customData,
  ...getMetaAttribution(),
  page_path: safeWindow?.location?.pathname,
});

const invokeFbq = (method, eventName, params = {}, eventId) => {
  if (!safeWindow || typeof safeWindow.fbq !== 'function') return;
  const payload = pruneObject(params);
  const options = eventId ? { eventID: eventId } : undefined;
  safeWindow.fbq(method, eventName, payload, options);
};

export const sendMetaServerEvent = async ({
  eventName,
  eventId,
  customData = {},
  userData = {},
  actionSource = 'website',
  eventSourceUrl = getEventSourceUrl(),
}) => {
  if (!safeWindow?.fetch) return;
  try {
    const token = safeWindow.localStorage?.getItem('token');
    await safeWindow.fetch('/api/analytics/meta/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_source_url: eventSourceUrl,
        action_source: actionSource,
        custom_data: buildCustomData(customData),
        user_data: buildUserData(userData),
      }),
      keepalive: true,
    });
  } catch (_err) {
    // Best-effort only.
  }
};

export const trackMetaEvent = ({
  eventName,
  method = 'track',
  customData = {},
  userData = {},
  eventId = generateMetaEventId(eventName.toLowerCase()),
  sendServerEvent = true,
}) => {
  const payload = buildCustomData(customData);
  invokeFbq(method, eventName, payload, eventId);
  if (sendServerEvent) {
    void sendMetaServerEvent({
      eventName,
      eventId,
      customData,
      userData,
    });
  }
  return eventId;
};

export const trackPageView = (customData = {}) => {
  trackMetaEvent({
    eventName: 'PageView',
    customData,
    sendServerEvent: false,
  });
};

export const trackViewContent = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'ViewContent', customData, userData, eventId })
);

export const trackLead = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'Lead', customData, userData, eventId })
);

export const trackCompleteRegistration = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'CompleteRegistration', customData, userData, eventId })
);

export const trackContact = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'Contact', customData, userData, eventId })
);

export const trackInitiateCheckout = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'InitiateCheckout', customData, userData, eventId })
);

export const trackPurchase = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'Purchase', customData, userData, eventId })
);

export const trackSchedule = (customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName: 'Schedule', customData, userData, eventId })
);

export const trackCustomMetaEvent = (eventName, customData = {}, userData = {}, eventId) => (
  trackMetaEvent({ eventName, method: 'trackCustom', customData, userData, eventId })
);
